'use client'

/** Ported from `nivex-design/apps/nivex-web-v3/src/components/ScoreTierBar.tsx`. */

import { cn } from '@/lib/utils'
import { WEALTH_SCORE_BANDS } from '@/features/cases/lib/wealth-score'

/** Ticks the audit page has always drawn under the tier bar. */
const SCALE_TICKS = [0, 25, 50, 75, 100]

const clampScore = (n: number) => Math.max(0, Math.min(100, n))

/**
 * The five wealth-score bands as one pill each, filled in that band's own colour up to `score` —
 * the shared "you are here" bar behind both the investor's prescription view and the dashboard's
 * growth-plan card, so the two never drift apart visually.
 *
 * `reach` optionally paints a second, weaker fill out to a score a plan could carry the investor to.
 * Because it is drawn per band, the headroom shows up in the colour of whichever band it reaches
 * into, rather than as a neutral extension.
 *
 * Band minimums are all multiples of twenty and every pill spans exactly one band, so a band
 * threshold is always the gap between two pills — there is no marker to position.
 */
export function ScoreTierBar({
  score,
  reach,
  showScale = false,
  className,
}: {
  score: number
  reach?: number
  showScale?: boolean
  className?: string
}) {
  const value = clampScore(score)
  const reachValue = reach != null ? clampScore(reach) : null

  const bar = (
    <div className={cn('flex h-1.5 gap-0.5', className)}>
      {WEALTH_SCORE_BANDS.map((band, i) => {
        const upper = WEALTH_SCORE_BANDS[i + 1]?.min ?? 100
        const span = upper - band.min
        const widthIn = (v: number) => `${clampScore(((v - band.min) / span) * 100)}%`
        return (
          // --border-primary, not --muted, for the unfilled remainder: the muted surfaces are within
          // a few RGB points of white in the light theme, so a muted track on a white panel reads as
          // 1.05:1 — no track at all. This is a real mid-tone in both themes.
          <span
            key={band.label}
            className="relative h-full flex-1 overflow-hidden first:rounded-l-full last:rounded-r-full"
            style={{ backgroundColor: 'var(--border-primary)' }}
          >
            {reachValue != null ? (
              <span
                className="absolute inset-y-0 left-0 rounded-full"
                style={{
                  width: widthIn(reachValue),
                  backgroundColor: `color-mix(in oklch, ${band.color} 35%, transparent)`,
                }}
              />
            ) : null}
            <span
              className="absolute inset-y-0 left-0 rounded-full transition-[width]"
              style={{ width: widthIn(value), backgroundColor: band.color }}
            />
          </span>
        )
      })}
    </div>
  )

  if (!showScale) return bar

  return (
    <div className="flex flex-col gap-1.5">
      {bar}
      <div className="text-muted-foreground flex justify-between text-[10px]">
        {SCALE_TICKS.map((tick) => (
          <span key={tick}>{tick}</span>
        ))}
      </div>
    </div>
  )
}
